import moment from "moment/moment";
import { useEffect, useState } from "react";
import {
  ALL_EVENT_INSTANCES,
  FUTURE_EVENT_INSTANCES,
  SINGLE_EVENT_INSTANCE,
} from "../../../../constants";
import returnRecurrenceDates from "../../../../helpers/returnRecurrenceDates";
import { useRemoveFromCalendar } from "../../../../hooks/api/calendar";
import { useRemoveFromTaskList } from "../../../../hooks/api/tasks";
import { toastError, toastMessage } from "../../Toast/Toast";

const useDeleteRecurrencePopupData = ({ popupData, closePopup }) => {
  const { removeFromCalendar } = useRemoveFromCalendar();
  const { deleteFromTaskList } = useRemoveFromTaskList();

  // Set the initial state of the radio select options
  const [radioSelectOptions, setSelectOptions] = useState({
    [SINGLE_EVENT_INSTANCE]: false,
    [ALL_EVENT_INSTANCES]: false,
    [FUTURE_EVENT_INSTANCES]: false,
  });

  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedEventDate, setSelectedEventDate] = useState({
    value: null,
    label: null,
  });

  useEffect(() => {
    if (
      radioSelectOptions[SINGLE_EVENT_INSTANCE] === true ||
      radioSelectOptions[FUTURE_EVENT_INSTANCES] === true
    ) {
      setSelectedEventDate({
        value: popupData?.item?.instanceStartDate,
        label: moment(popupData?.item?.instanceStartDate).format(
          "dddd, MMM Do YYYY, h:mm a"
        ),
      });
    }
  }, [popupData?.item?.instanceStartDate, radioSelectOptions]);

  // Set the active option
  const setOptionActive = (option) => {
    setSelectOptions({
      [SINGLE_EVENT_INSTANCE]: false,
      [ALL_EVENT_INSTANCES]: false,
      [FUTURE_EVENT_INSTANCES]: false,
      [option]: true,
    });
  };

  // Show the date select dropdown if the user has selected either
  const showDropDownDateSelect =
    radioSelectOptions[FUTURE_EVENT_INSTANCES] === true ||
    radioSelectOptions[SINGLE_EVENT_INSTANCE] === true;

  const recurrenceDates = returnRecurrenceDates({
    recurrence: popupData?.item?.recurrence,
  });

  // Map over the recurrence dates to format them for display.
  const formattedDatesArr = recurrenceDates.map((date) => {
    // Convert each date to a moment object, apply the user's guessed timezone,
    // and then format it to a user-friendly string.
    const formattedDate = moment(date)
      .tz(moment.tz.guess())
      .format("dddd, MMM Do YYYY, h:mm a");

    // Return an object with the formatted date as a label and the original date as a value.
    return { label: formattedDate, value: date };
  });

  const handleSuccessfulSave = (resource, type) => {
    toastMessage(`${resource} ${type} successfully`);
    closePopup();
  };

  const { pathname } = window.location;

  const deleteSingleItem = async () => {
    const { resource } = popupData.item;
    try {
      const deleteArgs = {
        item: popupData.item,
        date: selectedEventDate.value,
      };

      const deleteParams = {
        args: deleteArgs,
        operation: "$single",
      };

      // specific to if you are on the main calendar page
      if (pathname === "/calendar") {
        await removeFromCalendar(deleteParams);

        if (popupData.item.resource === "Task") {
          deleteFromTaskList(deleteParams, false);
        }
      }
      // specific to editing from the tasks page
      if (pathname.includes("/tasks")) {
        deleteFromTaskList(deleteParams);
        await removeFromCalendar(deleteParams);
      }

      // below handles anytime you are on a task or calendar page
      const urlObj = new URL(window.location.href);
      const tab = urlObj.searchParams.get("tab");

      if (tab === "tasks") {
        deleteFromTaskList(deleteParams);
        await removeFromCalendar(deleteParams);
      }

      if (tab === "calendar") {
        await removeFromCalendar(deleteParams);
        if (popupData.item.resource === "Task") {
          deleteFromTaskList(deleteParams);
        }
      }

      handleSuccessfulSave(resource, "deleted");
    } catch (error) {
      // Notify the user that there was an error updating the event.
      toastError("Error updating event");
    }
  };

  const deleteFutureItems = async () => {
    try {
      const deleteArgs = {
        item: popupData.item,
        date: selectedEventDate.value,
      };

      const deleteParams = {
        args: deleteArgs,
        operation: "$future",
      };

      // below will handle if the path start at /Calendar or /Task
      if (pathname === "/calendar") {
        await removeFromCalendar(deleteParams);

        if (popupData.item.resource === "Task") {
          deleteFromTaskList(deleteParams, false);
        }
      }

      if (pathname.includes("/tasks")) {
        deleteFromTaskList(deleteParams);
        await removeFromCalendar(deleteParams);
      }

      // below handles anytime you are on a task or calendar tab from a resource overview page (properties, spaces, etc)
      const urlObj = new URL(window.location.href);
      const tab = urlObj.searchParams.get("tab");

      if (tab === "tasks") {
        deleteFromTaskList(deleteParams);
        await removeFromCalendar(deleteParams);
      }

      if (tab === "calendar") {
        await removeFromCalendar(deleteParams);
        deleteFromTaskList(deleteParams, false);
      }

      toastMessage("Deleted successfully");
      closePopup();
    } catch (error) {
      // Notify the user that there was an error updating the event.
      toastError("Error updating event");
    }
  };

  const handleDeleteAllItems = async () => {
    try {
      const deleteParams = { args: popupData.item, operation: "$all" };

      // specific to if you are on the main calendar page
      if (pathname === "/calendar") {
        await removeFromCalendar(deleteParams);

        if (popupData.item.resource === "Task") {
          deleteFromTaskList(deleteParams, false);
        }
      }
      // specific to editing from the tasks page
      if (pathname.includes("/tasks")) {
        await removeFromCalendar(deleteParams);
        deleteFromTaskList(deleteParams, false);
      }

      // below handles anytime you are on a task or calendar page
      const urlObj = new URL(window.location.href);
      const tab = urlObj.searchParams.get("tab");

      if (tab === "tasks") {
        deleteFromTaskList(deleteParams);
        await removeFromCalendar(deleteParams);
      }

      if (tab === "calendar") {
        await removeFromCalendar(deleteParams);
        deleteFromTaskList(deleteParams, false);
      }

      handleSuccessfulSave(popupData.item.resource, "deleted");
    } catch (error) {
      toastError("Error deleting task");
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (radioSelectOptions[SINGLE_EVENT_INSTANCE] === true) {
      await deleteSingleItem();
    }

    if (radioSelectOptions[ALL_EVENT_INSTANCES] === true) {
      await handleDeleteAllItems();
    }

    if (radioSelectOptions[FUTURE_EVENT_INSTANCES] === true) {
      await deleteFutureItems();
    }

    // if the popupData has a handleCleanExit function, call it.
    if (popupData.handleCleanExit) {
      popupData.handleCleanExit();
    }

    // Set loading to false.
    setLoading(false);
  };

  const hasChosenOption = () => {
    if (radioSelectOptions[SINGLE_EVENT_INSTANCE] === true) {
      return false;
    }
    if (radioSelectOptions[ALL_EVENT_INSTANCES] === true) {
      return false;
    }

    if (radioSelectOptions[FUTURE_EVENT_INSTANCES] === true) {
      return false;
    }

    return true;
  };

  const noOptionChosen = hasChosenOption();

  return {
    loading,
    radioSelectOptions,
    showDropDownDateSelect,
    formattedDatesArr,
    selectedEventDate,
    noOptionChosen,
    handleSubmit,
    setOptionActive,
    setSelectedEventDate,
    setIsDropdownOpen,
    isDropdownOpen,
  };
};

export default useDeleteRecurrencePopupData;
